import { Save, Restore, DeleteForever } from '@mui/icons-material';
import { Divider, Fade, Grid, MenuItem, Typography, Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useState, useMemo } from 'react';
// Components
import { Modal, Loader, SMBSelect } from '../../../components';
import { UserContext } from '../../../context';
import {
  createPlaceOrderUser,
  getPlaceOrderAvailableReportsTo,
  updatePlaceOrderUser,
  deletePlaceOrderUser,
  resetTwoFactorAuth,
  getAccessInformation
} from '../../../fetch';
import { isAllowedToBeEditedOrDeleted, alphaSort } from '../../../helpers';
import {
  IAccessInformation,
  IDomainUser,
  IDropdownResponse,
  IManagePlaceOrderReportsTo
} from '../../../models';
import { AccordionList } from '../../../components/accordion/simple-accordion-list';
import { getAdminLookupPlaceOrderRoles } from '../../../fetch/lookups';

interface IInternalUserModal {
  open: boolean;
  onClose: (shouldUpdate?: boolean) => void;
  currentUser?: IDomainUser;
  fetchUsers?: () => void;
}

export const InternalUserModal: FC<IInternalUserModal> = ({
  open,
  onClose,
  currentUser,
  fetchUsers
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, loadUser } = useContext(UserContext);
  const [selectedRole, setSelectedRole] = useState<string | number | undefined>(
    currentUser?.placeOrderRole?.placeOrderRoleId ?? ''
  );
  const [selectedReportsTo, setSelectedReportsTo] = useState<string | undefined>(
    currentUser?.reportsTo?.accountName ?? ''
  );
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const [isResetting, setResetting] = useState(false);
  const [isLoadingAvailableReportsTo, setIsLoadingAvailableReportsTo] = useState(false);
  const [availableReportsTo, setAvailableReportsTo] = useState<IManagePlaceOrderReportsTo[]>([]);
  const [roles, setRoles] = useState<IDropdownResponse[]>([]);

  const isDirty =
    selectedRole === currentUser?.placeOrderRole?.placeOrderRoleId ||
    selectedReportsTo === currentUser?.reportsTo?.accountName;

  const handleResetAuth = async (original: IDomainUser, showLoader?: boolean) => {
    const result = window.confirm('Are you sure you want to reset two factor auth for this user?');
    if (result) {
      if (showLoader) {
        setResetting(true);
      }
      try {
        await resetTwoFactorAuth(original.accountName);
        enqueueSnackbar('Two factor auth reset!', {
          variant: 'success'
        });
      } catch (error: any) {
        const variant =
          error?.Detail &&
          (error.Detail.includes('User not enrolled in MFA') ||
            error.Detail.includes('All user MFA enrollments inactive'))
            ? 'warning'
            : 'error';
        enqueueSnackbar(error?.Detail ?? 'User does not exist', {
          variant: variant
        });
      } finally {
        setResetting(false);
      }
    }
  };

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const placeOrderRoles = await getAdminLookupPlaceOrderRoles();
      setRoles(alphaSort(placeOrderRoles, 'description'));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const hasReportsTo = useMemo(
    () =>
      isAllowedToBeEditedOrDeleted(
        roles?.filter(role => +role.value === selectedRole)[0]?.description
      ),
    [roles, selectedRole]
  );

  const getAvailableReportsTo = async (accountName: string) => {
    try {
      setIsLoadingAvailableReportsTo(true);
      const res = await getPlaceOrderAvailableReportsTo(accountName);
      setAvailableReportsTo(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAvailableReportsTo(false);
    }
  };

  const handleClose = (shouldReload?: boolean, showWarning = true) => {
    if (
      selectedRole !== (currentUser?.placeOrderRole?.placeOrderRoleId ?? '') ||
      selectedReportsTo !== (currentUser?.reportsTo?.accountName ?? '')
    ) {
      if (showWarning) {
        const result = window.confirm('You have unsaved changes, are you sure you want to exit?');

        if (result) {
          setSelectedReportsTo('');
          setSelectedRole('');
          onClose(shouldReload ?? false);
          return;
        }
        return;
      }
    }
    onClose(shouldReload ?? false);
  };

  const handleDeleteRole = async () => {
    const result = window.confirm('Are you sure you want to remove this role?');
    if (result) {
      try {
        setIsDeletingRole(true);
        await deletePlaceOrderUser(currentUser.accountName);
        fetchUsers && fetchUsers();
        handleClose(true);
        enqueueSnackbar('User Updated!', {
          variant: 'success'
        });
      } catch (error) {
        return enqueueSnackbar(
          error?.Detail ??
            'Error, we were unable to update the user at this time. Please try again later.',
          {
            variant: 'error'
          }
        );
      } finally {
        setIsDeletingRole(false);
      }
    }
  };

  const handleSaveRole = async () => {
    try {
      setIsSaving(true);
      const data = {
        displayName: currentUser.displayName,
        placeOrderRoleId: Number(selectedRole),
        reportsToAccountName: !hasReportsTo ? null : selectedReportsTo
      };
      currentUser?.placeOrderRole
        ? await updatePlaceOrderUser(currentUser.accountName, data)
        : await createPlaceOrderUser(currentUser.accountName, data);

      fetchUsers && fetchUsers();
      if (user.userName === currentUser.accountName) loadUser(true);
      handleClose(true, false);
      enqueueSnackbar('User Updated!', {
        variant: 'success'
      });
    } catch (error) {
      return enqueueSnackbar(
        error?.Detail ??
          'Error, we were unable to update the user at this time. Please try again later.',
        {
          variant: 'error'
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const [isLoadingAccessInformation, setIsLoadingAccessInformation] = useState(false);
  const [userAccessInformation, setUserAccessInformation] = useState<IAccessInformation | null>(
    null
  );
  const getUserAccessInformation = async (accountName: string) => {
    try {
      setIsLoadingAccessInformation(true);
      const res = await getAccessInformation(accountName);
      setUserAccessInformation(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAccessInformation(false);
    }
  };

  useEffect(() => {
    if (selectedRole && currentUser) {
      getAvailableReportsTo(currentUser.accountName);
    }
    if (currentUser) getUserAccessInformation(currentUser.accountName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose(false, isDirty);
        }}
        maxWidth="sm"
      >
        <Fade in={open}>
          <Box mt={1}>
            {(isLoadingRoles ||
              isSaving ||
              isResetting ||
              isLoadingAvailableReportsTo ||
              isLoadingAccessInformation ||
              isDeletingRole) && <Loader position="centered" type="overlay" />}
            <Box marginBottom="0.5rem" display="flex" flexWrap="wrap">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">Edit {currentUser?.displayName}</Typography>
              </Box>
              {!!currentUser && (
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <LoadingButton
                    type="button"
                    startIcon={<Restore />}
                    disabled={isResetting}
                    loading={isResetting}
                    loadingPosition="start"
                    color="secondary"
                    onClick={async () => {
                      setResetting(true);
                      await handleResetAuth(currentUser, false);
                      setResetting(false);
                    }}
                  >
                    Reset Two Factor Auth
                  </LoadingButton>
                </Box>
              )}
            </Box>
            <Divider />
            <Grid container spacing={1} sx={{ marginTop: '0.5rem' }}>
              <Grid item xs={12} sm={hasReportsTo ? 6 : 12}>
                <SMBSelect
                  label="Role"
                  required
                  handleClear={() => setSelectedRole('')}
                  value={selectedRole}
                  disabled={isLoadingRoles}
                  onChange={event => {
                    setSelectedRole(Number(event.target.value));
                  }}
                >
                  {roles?.map((role, index) => {
                    return (
                      <MenuItem key={`${index}`} value={role.value}>
                        {role.description}
                      </MenuItem>
                    );
                  })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} sm={6}>
                {hasReportsTo && (
                  <SMBSelect
                    disabled={isLoadingAvailableReportsTo}
                    label="Reports to"
                    required={!hasReportsTo}
                    handleClear={() => setSelectedReportsTo('')}
                    value={selectedReportsTo}
                    onChange={event => {
                      setSelectedReportsTo(event.target.value);
                    }}
                  >
                    {availableReportsTo?.map((reportsTo, index) => {
                      return (
                        <MenuItem key={`${index}`} value={reportsTo.accountName}>
                          {reportsTo.displayName}
                        </MenuItem>
                      );
                    })}
                  </SMBSelect>
                )}
              </Grid>
              <Grid item xs={12}>
                {userAccessInformation?.roles && (
                  <AccordionList
                    pluralItemName={'Security Roles'}
                    title={`Security Roles (${userAccessInformation?.roles.length})`}
                    items={userAccessInformation?.roles?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
                {userAccessInformation?.territories && (
                  <AccordionList
                    pluralItemName={'Territories'}
                    title={`Territories (${
                      Array.from(
                        new Set(
                          userAccessInformation?.territories.flatMap(
                            territory => territory.territoryNames
                          )
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.territories.flatMap(
                          territory => territory.territoryNames
                        )
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
                {userAccessInformation?.neighborhoods && (
                  <AccordionList
                    pluralItemName={'Neighborhoods'}
                    title={`Neighborhoods (${
                      Array.from(
                        new Set(
                          userAccessInformation?.neighborhoods.flatMap(
                            neighborhood => neighborhood.neighborHoods
                          )
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.neighborhoods.flatMap(
                          neighborhood => neighborhood.neighborHoods
                        )
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
                {userAccessInformation?.jobs && (
                  <AccordionList
                    pluralItemName={'Job Assignments'}
                    title={`Job Assignments (${
                      Array.from(
                        new Set(
                          userAccessInformation?.jobs
                            .flatMap(job => job.jobs)
                            .map(job => `${job.neighborhoodName}-${job.lotNumber}`)
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.jobs
                          .flatMap(job => job.jobs)
                          .map(job => `${job.neighborhoodName}-${job.lotNumber}`)
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
              </Grid>
            </Grid>
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isSaving || (hasReportsTo && !selectedReportsTo)}
                  startIcon={<Save />}
                  onClick={handleSaveRole}
                >
                  Save
                </Button>
              </Box>
              {currentUser?.placeOrderRole && (
                <LoadingButton
                  color="error"
                  type="button"
                  variant="contained"
                  disabled={isDeletingRole || (hasReportsTo && !selectedReportsTo)}
                  startIcon={<DeleteForever />}
                  loading={isDeletingRole}
                  loadingPosition="start"
                  onClick={handleDeleteRole}
                >
                  Remove Role
                </LoadingButton>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
